import {
  faChevronDown,
  faChevronUp,
  faEnvelope,
  faEnvelopeOpen,
  faMousePointer,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Spinner } from "reactstrap";
import { useRouterCore } from "../../../../cores/router";
import {
  platformAutomationsApi,
  PlatformAutomationUserEmailRow,
} from "../../../../cores/platformAutomations/services/api";

interface Props {
  userId: string;
}

const engagementStatus = (email: PlatformAutomationUserEmailRow) => {
  if (email.clickedAt || (email.clickCount || 0) > 0) return "clicked";
  if (email.openedAt || (email.openCount || 0) > 0) return "opened";
  if (email.deliveredAt) return "delivered";
  return "sent";
};

export default function UserAutomationEmails({ userId }: Props) {
  const intl = useIntl();
  const { goToPlatformAutomationEdit } = useRouterCore();
  const [emails, setEmails] = useState<PlatformAutomationUserEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    platformAutomationsApi
      .listUserEmails(userId)
      .then((rows) => {
        if (!cancelled) setEmails(rows || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return intl.formatDate(date, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusLabel = (status: string) =>
    intl.formatMessage({
      id: `app.users.communicationStatus${status[0].toUpperCase()}${status.slice(
        1
      )}`,
    });

  return (
    <section className="user-email-history">
      <div className="user-email-history__header">
        <div>
          <h5>
            <FontAwesomeIcon icon={faEnvelope} />
            {intl.formatMessage({ id: "app.users.communicationHistory" })}
          </h5>
          <p>
            {intl.formatMessage({ id: "app.users.communicationHistoryHint" })}
          </p>
        </div>
        {!loading && (
          <span className="user-email-history__count">{emails.length}</span>
        )}
      </div>

      {loading ? (
        <div className="user-email-history__state">
          <Spinner size="sm" color="primary" />
        </div>
      ) : emails.length === 0 ? (
        <div className="user-email-history__state">
          {intl.formatMessage({ id: "app.users.communicationHistoryEmpty" })}
        </div>
      ) : (
        <div className="user-email-history__list">
          {emails.map((email) => {
            const expanded = expandedId === email.id;
            const status = engagementStatus(email);
            return (
              <article className="user-email-history__item" key={email.id}>
                <button
                  type="button"
                  className="user-email-history__summary"
                  onClick={() => setExpandedId(expanded ? null : email.id)}
                  aria-expanded={expanded}
                >
                  <span className="user-email-history__mail-icon">
                    <FontAwesomeIcon
                      icon={
                        status === "opened" || status === "clicked"
                          ? faEnvelopeOpen
                          : faEnvelope
                      }
                    />
                  </span>
                  <span className="user-email-history__main">
                    <strong>
                      {email.subject ||
                        intl.formatMessage({
                          id: "app.users.communicationNoSubject",
                        })}
                    </strong>
                    <small>
                      {email.automationName} · {formatDate(email.sentAt)}
                    </small>
                    <span
                      className={`user-email-history__status user-email-history__status--${status}`}
                    >
                      {statusLabel(status)}
                      {(email.openCount || 0) > 0 && (
                        <>
                          {" · "}
                          {intl.formatMessage(
                            { id: "app.users.communicationOpenCount" },
                            { count: email.openCount }
                          )}
                        </>
                      )}
                      {(email.clickCount || 0) > 0 && (
                        <>
                          {" · "}
                          {intl.formatMessage(
                            { id: "app.users.communicationClickCount" },
                            { count: email.clickCount }
                          )}
                        </>
                      )}
                    </span>
                  </span>
                  <FontAwesomeIcon
                    className="user-email-history__chevron"
                    icon={expanded ? faChevronUp : faChevronDown}
                  />
                </button>

                {expanded && (
                  <div className="user-email-history__detail">
                    <dl>
                      <div>
                        <dt>
                          {intl.formatMessage({
                            id: "app.users.communicationAutomation",
                          })}
                        </dt>
                        <dd>{email.automationName}</dd>
                      </div>
                      {email.templateName && (
                        <div>
                          <dt>
                            {intl.formatMessage({
                              id: "app.users.communicationTemplate",
                            })}
                          </dt>
                          <dd>{email.templateName}</dd>
                        </div>
                      )}
                      <div>
                        <dt>
                          {intl.formatMessage({
                            id: "app.users.communicationSentAt",
                          })}
                        </dt>
                        <dd>{formatDate(email.sentAt)}</dd>
                      </div>
                      <div>
                        <dt>
                          {intl.formatMessage({
                            id: "app.users.communicationDeliveredAt",
                          })}
                        </dt>
                        <dd>{formatDate(email.deliveredAt)}</dd>
                      </div>
                      <div>
                        <dt>
                          {intl.formatMessage({
                            id: "app.users.communicationOpenedAt",
                          })}
                        </dt>
                        <dd>{formatDate(email.openedAt)}</dd>
                      </div>
                      <div>
                        <dt>
                          <FontAwesomeIcon
                            icon={faMousePointer}
                            className="mr-1"
                          />
                          {intl.formatMessage({
                            id: "app.users.communicationClickedAt",
                          })}
                        </dt>
                        <dd>{formatDate(email.clickedAt)}</dd>
                      </div>
                    </dl>
                    <p className="user-email-history__preview">
                      {email.contentPreview ||
                        intl.formatMessage({
                          id: "app.users.communicationContentUnavailable",
                        })}
                    </p>
                    <button
                      type="button"
                      className="user-email-history__automation-link"
                      onClick={() =>
                        goToPlatformAutomationEdit(email.automationId)
                      }
                    >
                      {intl.formatMessage({
                        id: "app.users.communicationViewAutomation",
                      })}
                    </button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
